import React, { useCallback, useState, useEffect } from 'react';
import { useBoundStore } from '@/stores';
import i18n from '@/lib/i18n';
import { useLogout, useRefreshSession } from '../api/queries';
import { useActivityTracker } from '../hooks/useActivityTracker';
import { useSessionTimeout, TimeoutType } from '../hooks/useSessionTimeout';
import { SessionWarningModal } from '../components/SessionWarningModal';
import { setSessionExpiredReason } from '../components/SessionExpiredDialog';
import { toast } from 'sonner';
import { getErrorMessage } from '@/shared/utils/commonUtils';
import { environment } from '@/config/environment';

interface SessionTimeoutProviderProps {
    children: React.ReactNode;
}

export const SessionTimeoutProvider: React.FC<SessionTimeoutProviderProps> = ({ children }) => {
    const user = useBoundStore.use.user();
    const logoutMutation = useLogout();
    const refreshSessionMutation = useRefreshSession();
    const [stayLoading, setStayLoading] = useState(false);
    const [signOutLoading, setSignOutLoading] = useState(false);
    const [autoLogoutLoading, setAutoLogoutLoading] = useState(false);

    // Handle Logout
    const handleLogout = useCallback((reason: 'idle' | 'absolute' | 'manual') => {
        if (reason === 'manual') {
            setSignOutLoading(true);
        } else {
            setAutoLogoutLoading(true);
        }

        // Store reason for dialog on login page (for auto-logout only)
        if (reason === 'idle' || reason === 'absolute') {
            setSessionExpiredReason(reason);
        }

        logoutMutation.mutate(undefined, {
            onSettled: () => {
                setSignOutLoading(false);
                setAutoLogoutLoading(false);
            }
        });
    }, [logoutMutation]);

    // Session Management Hook
    const {
        warningType,
        timeLeft,
        registerActivity,
        beginIdleExtension,
        endIdleExtension
    } = useSessionTimeout({
        sessionMetadata: user?.session,
        onIdleExpire: () => handleLogout('idle'),
        onAbsoluteExpire: () => handleLogout('absolute'),
        warningThresholdMinutes: environment.SESSION_WARNING_MINUTES
    });

    // Track previous user to detect login transition
    const prevUserRef = React.useRef(user);
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        const justLoggedIn = !prevUserRef.current && !!user;
        prevUserRef.current = user;

        // Show warning modal only for IDLE (user can extend). For ABSOLUTE we do not show the timer modal;
        // when absolute expiry hits we logout and show "session expired" on login page.
        if (user && warningType === TimeoutType.IDLE && !justLoggedIn) {
            setShowModal(true);
        } else {
            setShowModal(false);
        }
    }, [user, warningType]);



    // Track User Activity (mouse, keyboard, etc.)
    useActivityTracker({
        onActivity: registerActivity,
        isActive: !!user?.session && warningType === TimeoutType.NONE,
        debounceMs: 1000
    });

    // Handle "Stay Signed In" Click
    const handleStaySignedIn = async () => {
        setStayLoading(true);
        beginIdleExtension();
        try {
            await refreshSessionMutation.mutateAsync();
            registerActivity();
            toast.success(i18n.t('toasts.sessionExtended', { ns: 'common' }));
        } catch (error) {
            toast.error(getErrorMessage(error, i18n.t('toasts.sessionExtendFailed', { ns: 'common' })));
        } finally {
            endIdleExtension();
            setStayLoading(false);
        }
    };

    // Handle Manual Sign Out from Modal
    const handleSignOut = () => {
        handleLogout('manual');
    };

    return (
        <>
            {children}
            <SessionWarningModal
                isOpen={showModal}
                timeoutType={warningType}
                timeLeft={timeLeft}
                onStaySignedIn={handleStaySignedIn}
                onSignOut={handleSignOut}
                stayLoading={stayLoading}
                signOutLoading={signOutLoading || autoLogoutLoading}
            />
        </>
    );
};
